/**
 * displayPrefs.js — single source of truth for what's toggleable across the app
 *
 * Each toggleable section ("widget") has:
 *   - id           kebab-case unique within its page
 *   - label        user-facing name in the settings page
 *   - desc         optional one-liner shown under the label
 *   - adaptiveOnly hide unless adaptiveMode is on
 *   - requiresNutrition hide unless nutrition is set up (nutritionGoal exists)
 *   - recommendedFor only meaningful if user's adaptiveSubmodes intersects this list
 *
 * Visibility resolution:
 *   1. Hard floor — adaptiveOnly / requiresNutrition / recommendedFor must pass.
 *   2. Explicit user override (true/false) wins over everything else.
 *   3. Simplified preset → only whitelisted widgets show.
 *   4. Otherwise → show by default.
 *
 * preset values: 'default' | 'simplified'
 *   - 'simplified' is auto-set during onboarding for users who flag
 *     'older-adult' in adaptiveSubmodes. Users can opt in/out anytime.
 */

export const WIDGET_CATALOG = {
  modules: {
    label: 'App modules',
    desc: 'Hide entire sections from your navigation. Direct URLs still work.',
    widgets: [
      { id: 'recovery', label: 'Endurance', desc: 'Walk/run, cycling, hiking, swimming, climbing, etc.' },
      { id: 'training', label: 'Combatives', desc: 'Martial arts tracking with ranks.' },
      { id: 'nutrition', label: 'Nutrition', desc: 'Calorie + macro tracking.' },
      { id: 'coach', label: 'Coach', desc: 'Ask Enferno AI conversations.' },
      { id: 'library', label: 'Library', desc: 'Exercise library + The Vault gear directory.' },
    ],
  },
  dashboard: {
    label: 'Progress',
    widgets: [
      { id: 'pacingBanner', label: 'Pacing Banner', desc: 'Daily flare-aware pacing nudge' },
      { id: 'dailyMotivator', label: 'Daily Motivator', desc: 'Quote-style daily prompt' },
      { id: 'macroNudge', label: 'Pre-workout fuel reminder', desc: 'Pings before training', requiresNutrition: true },
      { id: 'wellbeing', label: 'Wellbeing Check-in', desc: 'Energy / sleep / mood / function' },
      { id: 'painCheck', label: 'Pain Check-in', desc: 'Daily pain rating' },
      { id: 'stats', label: 'Stats strip', desc: 'Workouts · Endurance · Goal · PRs' },
      { id: 'muscleRecovery', label: 'Muscle Recovery card' },
      { id: 'todaysFuel', label: "Today's Fuel tile", desc: 'Calories + macros snapshot', requiresNutrition: true },
      { id: 'macroPattern', label: 'Macro Pattern · last 7 days', desc: 'Daily macro fingerprint (kcal/P/C/F)', requiresNutrition: true },
      { id: 'weeklyChart', label: 'Weekly Chart' },
      { id: 'recentWorkouts', label: 'Recent Workouts list' },
    ],
  },
  strength: {
    label: 'Strength',
    widgets: [
      { id: 'sessionLengthPicker', label: 'Session length quick-swap', desc: '30 / 45 / 60 min one-tap picker' },
      { id: 'autosaveIndicator', label: 'Autosave indicator', desc: '"X sets saved locally" strip' },
      { id: 'coachTip', label: "Coach's Tip", desc: 'Per-day-type guidance card above the exercise list' },
      { id: 'restTimer', label: 'Rest Timer', desc: 'Right-rail circular rest timer + quick-start buttons' },
      { id: 'todaysEffort', label: "Today's Effort summary", desc: 'Sets completed · volume lifted · exercises done' },
    ],
  },
  recovery: {
    label: 'Endurance',
    widgets: [
      { id: 'recentSessions', label: 'Recent sessions carousel' },
      { id: 'affiliateGear', label: 'Gear recommendations strip' },
    ],
  },
  training: {
    label: 'Combatives',
    widgets: [
      { id: 'affiliateGear', label: 'Per-discipline gear recommendations', desc: 'Gi, gloves, wraps, shin guards, etc.' },
      { id: 'pastSessions', label: 'Past sessions list', desc: 'Your logged session history for the active discipline' },
    ],
  },
  nutrition: {
    label: 'Nutrition',
    widgets: [
      { id: 'supplementAffiliates', label: 'Supplement recommendations' },
    ],
  },
};

const SIMPLIFIED_WHITELIST = new Set([
  // Dashboard — keep only the essentials
  'dashboard.pacingBanner',
  'dashboard.stats',
  'dashboard.weeklyChart',
  'dashboard.recentWorkouts',
  'dashboard.wellbeing',
  'dashboard.painCheck',
  // Strength stays usable
  'strength.autosaveIndicator',
]);

// Skeleton Mode — even more aggressive than Simplified. Only the absolute
// minimum widgets show. Everything else hidden by default. User must
// explicitly opt-in any extras via Display Preferences.
//
// Per user direction: even the stats strip, weekly chart, and
// recent workouts are hidden in Skeleton — user wants the dashboard close
// to empty so they can opt in only what they actually want. Only the
// in-workout autosave indicator stays on (safety net for unsaved sets).
const SKELETON_WHITELIST = new Set([
  // Strength keeps autosave indicator (non-negotiable — protects in-workout data)
  'strength.autosaveIndicator',
]);

// Skeleton Mode module allowlist — these 4 modules are ALWAYS visible in
// skeleton mode (cannot be hidden). Other modules (recovery, training,
// nutrition, coach) are OFF by default and must be explicitly enabled
// either by user toggle or by onboarding-signal smart defaults.
//
// Note: `dashboard` (Progress) + `strength` (Workout) are page-level, not
// modules in the Navbar sense; they're always visible. The 4 always-on
// modules below correspond to Navbar nav items with `module: null`
// (Progress, Workout, Library, Impact) plus we treat library as always-on
// per user direction.
const SKELETON_ALWAYS_ON_MODULES = new Set([
  'library', // always-on per user direction
]);
// recovery, training, nutrition, coach → all OFF by default in skeleton

// All pages/widgets as flat list — used by settings UI
export const flatWidgetList = () =>
  Object.entries(WIDGET_CATALOG).flatMap(([page, group]) =>
    group.widgets.map((w) => ({ page, pageLabel: group.label, ...w })),
  );

export const isWidgetVisible = (page, widgetId, profile) => {
  // Modules are a special case — they use onboarding-signal-derived smart defaults
  // when the user has no explicit pref set (handles legacy users).
  if (page === 'modules') return isModuleVisible(widgetId, profile);

  const cat = WIDGET_CATALOG[page]?.widgets.find((w) => w.id === widgetId);
  if (!cat) return true;

  // 1. Hard floor — conditions that override everything
  if (cat.adaptiveOnly && !profile.adaptiveMode) return false;
  if (cat.requiresNutrition && !profile.nutritionGoal) return false;
  if (cat.recommendedFor) {
    const subs = profile.adaptiveSubmodes || [];
    if (!cat.recommendedFor.some((s) => subs.includes(s))) return false;
  }

  const prefs = profile.displayPrefs || {};
  const explicit = prefs[page]?.[widgetId];

  // 2. Explicit user override wins
  if (explicit === true) return true;
  if (explicit === false) return false;

  // 3. Simplified preset = whitelist only
  if (prefs.preset === 'simplified') {
    return SIMPLIFIED_WHITELIST.has(`${page}.${widgetId}`);
  }

  // 3b. Skeleton preset = even tighter whitelist
  if (prefs.preset === 'skeleton') {
    return SKELETON_WHITELIST.has(`${page}.${widgetId}`);
  }

  // 4. Default = show
  return true;
};

/**
 * Compute the smart preset for a freshly-onboarded user.
 * Called once when onboarding completes.
 */
export const smartPresetForProfile = (profile) => {
  const subs = profile.adaptiveSubmodes || [];
  if (subs.includes('older-adult')) return 'simplified';
  return 'default';
};

// Compute default module visibility from onboarding signals. Called once when
// onboarding completes so users land on a clean nav with only relevant modules.
//
// Preset behavior:
//   - 'default' (Full):  every module visible. Smart-defaults are ignored —
//     when the user explicitly picks Full, "Full" means "show me everything."
//   - 'simplified':      use onboarding signals to hide modules the user
//     hasn't indicated interest in.
//   - 'skeleton':        modules are hidden per SKELETON_WHITELIST elsewhere;
//     this function still returns the smart-defaults for completeness so
//     they're recorded if the user later flips to Simplified.
//
// Onboarding signals used for the non-default case:
//   - recovery: has any selected recovery activity OR has 'endurance' goal
//   - nutrition: explicit `wantsNutrition === true` OR has nutritionGoal set
//   - training (Combatives): explicit Combatives goal OR has logged any
//     martial art previously
export const smartModulesForProfile = (profile) => {
  const preset = profile.displayPrefs?.preset || 'default';

  if (preset === 'default') {
    return { recovery: true, nutrition: true, training: true, library: true, coach: true };
  }

  const goals = profile.goals || [];
  const hasRecovery = (profile.recoveryActivities || []).length > 0 || goals.includes('endurance');
  const hasNutrition = profile.wantsNutrition === true || !!profile.nutritionGoal;
  const hasTraining = goals.includes('combatives') || (profile.martialArts || []).length > 0;
  return {
    recovery: hasRecovery,
    nutrition: hasNutrition,
    training: hasTraining,
    library: true,  // always on
    coach: true,    // always on
  };
};

// Is a top-level module visible for this user? Smart default + explicit override.
// Skeleton mode adds an extra gate: only `library` is always on; other modules
// (recovery, training, nutrition, coach) must be explicitly enabled.
export const isModuleVisible = (moduleId, profile) => {
  const prefs = profile.displayPrefs || {};
  const explicit = prefs.modules?.[moduleId];
  if (explicit === true) return true;
  if (explicit === false) return false;

  // Skeleton preset: only the always-on allowlist passes without explicit opt-in.
  // (Progress + Workout + Impact are non-module nav items in Navbar.jsx —
  // they're always rendered there regardless of this function.)
  if (prefs.preset === 'skeleton' && !SKELETON_ALWAYS_ON_MODULES.has(moduleId)) {
    return false;
  }

  // Fallback to smart defaults if no explicit pref set
  const defaults = smartModulesForProfile(profile);
  return defaults[moduleId] !== false;
};

export const defaultDisplayPrefs = () => ({ preset: 'default' });
